import { OVERLAP_STATUS } from "common/constants/overlap-status";
import React from "react";
import { Col, Row } from "reactstrap";

/**
 * Card copy for each resolution. Titles are shorter than the full labels in
 * common/constants/overlap-status so they fit on a card; the description tells
 * the reviewer what picking it does.
 */
const OVERLAP_STATUS_CARDS = [
  {
    value: OVERLAP_STATUS.CONFIRMED_DUPLICATE_PAYMENT,
    title: "Confirmed duplicate payment",
    description: "Payments will be marked as duplicate.",
    icon: "ri-file-copy-2-line",
    tone: "danger",
  },
  {
    value: OVERLAP_STATUS.NOT_DUPLICATE_COFUND,
    title: "Not a duplicate - Cofund",
    description: "Payments will be marked as a cofund between agencies.",
    icon: "ri-hand-coin-line",
    tone: "info",
  },
  {
    value: OVERLAP_STATUS.NOT_DUPLICATE_DOUBLE_ENTRY,
    title: "Not a duplicate - Double Entry",
    description: "The payments will be merged into one.",
    icon: "ri-git-merge-line",
    tone: "warning",
  },
];

/**
 * Picks an overlap status from a row of cards. Built on native radio inputs,
 * so it reads as a radio group to screen readers, arrow keys move between
 * cards, and Formik's handleChange/handleBlur work on it unchanged.
 */
const OverlapStatusCards: React.FC<{
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  labelledBy?: string;
  /** Keeps element ids unique when two forms on a page both show the cards. */
  idPrefix?: string;
  /** Statuses this form does not offer. */
  exclude?: string[];
}> = ({
  name,
  value,
  onChange,
  onBlur,
  invalid,
  labelledBy,
  idPrefix,
  exclude = [],
}) => {
  const cards = OVERLAP_STATUS_CARDS.filter(
    (card) => !exclude.includes(card.value)
  );
  // Share the row evenly however many cards are shown.
  const columnWidth = Math.floor(12 / cards.length);

  return (
    <Row
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-invalid={invalid || undefined}
      className={`g-3 overlap-status-cards${invalid ? " is-invalid" : ""}`}
    >
      {cards.map((card) => {
        const id = `${idPrefix ?? name}-${card.value}`;
        return (
          <Col md={columnWidth} key={card.value}>
            <input
              type="radio"
              className="overlap-status-card-input"
              id={id}
              name={name}
              value={card.value}
              checked={value === card.value}
              onChange={onChange}
              onBlur={onBlur}
              aria-describedby={`${id}-description`}
            />
            <label htmlFor={id} className="overlap-status-card">
              <span className="avatar-xs flex-shrink-0">
                <span
                  className={`avatar-title rounded bg-${card.tone}-subtle text-${card.tone} fs-16`}
                >
                  <i className={card.icon} aria-hidden="true" />
                </span>
              </span>
              <span className="overlap-status-card-title">{card.title}</span>
              <span
                id={`${id}-description`}
                className="overlap-status-card-description"
              >
                {card.description}
              </span>
              <i
                className="ri-checkbox-circle-fill overlap-status-card-check"
                aria-hidden="true"
              />
            </label>
          </Col>
        );
      })}
    </Row>
  );
};

export default OverlapStatusCards;
